import React, { useEffect, useMemo, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { SettingsMenu } from './SettingsMenu';
import { NavigationGuideModal } from './NavigationGuideModal';
import { AssistantOverlay } from './AssistantOverlay';
import { SelectionsProvider, useSelections } from './SelectionsContext';
import { SelectionsWidget } from './SelectionsWidget';
import { interiorMenuData, exteriorMenuData, InteriorMenuItem } from '../../types/interiorConfig';
import { LayoutStandard } from './Layouts/LayoutStandard';
import { NavigationRoomsPanel } from './NavigationRoomsPanel';
import { ElevationStrip } from '../shared/ElevationStrip';
import { ExteriorGallery } from './ExteriorGallery';
import { ExteriorPackageOption, ExteriorPackagePane } from './ExteriorPackagePane';
import { models, exteriorPalettes } from '../../data/mockData';
import { Elevation } from '../../types';
const EXTERIOR_ELEVATIONS: Elevation[] = [{
  id: 'elev-colonial',
  letter: 'A',
  name: 'Colonial',
  thumbnailUrl: 'https://images.unsplash.com/photo-1605276374104-dee2a0ed3cd6?auto=format&fit=crop&w=600&q=80'
}, {
  id: 'elev-craftsman',
  letter: 'B',
  name: 'Craftsman',
  thumbnailUrl: 'https://images.unsplash.com/photo-1600566753190-17f0baa2a6c3?auto=format&fit=crop&w=600&q=80'
}, {
  id: 'elev-farmhouse',
  letter: 'C',
  name: 'Farmhouse',
  thumbnailUrl: 'https://images.unsplash.com/photo-1600607687939-ce8a6c25118c?auto=format&fit=crop&w=600&q=80'
}, {
  id: 'elev-georgian',
  letter: 'D',
  name: 'Georgian Colonial',
  thumbnailUrl: 'https://images.unsplash.com/photo-1600573472550-8090b5e0745e?auto=format&fit=crop&w=600&q=80'
}, {
  id: 'elev-mountain',
  letter: 'E',
  name: 'Mountain',
  thumbnailUrl: 'https://images.unsplash.com/photo-1518780664697-55e3ad937233?auto=format&fit=crop&w=600&q=80'
}, {
  id: 'elev-prairie',
  letter: 'F',
  name: 'Prairie',
  thumbnailUrl: 'https://images.unsplash.com/photo-1564013799919-ab600027ffc6?auto=format&fit=crop&w=600&q=80'
}];
// Packages available per elevation (some elevations have multiple facade/structural packages)
const EXTERIOR_PACKAGES: Record<string, ExteriorPackageOption[]> = {
  'elev-colonial': [{
    id: 'pkg-vcr-2-1',
    code: 'VCR 2.1',
    thumbnail: 'https://images.unsplash.com/photo-1605276374104-dee2a0ed3cd6?auto=format&fit=crop&w=400&q=80',
    priceModifier: 0
  }, {
    id: 'pkg-vcr-3-1',
    code: 'VCR 3.1',
    thumbnail: 'https://images.unsplash.com/photo-1572120360610-d971b9d7767c?auto=format&fit=crop&w=400&q=80',
    priceModifier: 1200
  }, {
    id: 'pkg-vcr-5-1',
    code: 'VCR 5.1',
    thumbnail: 'https://images.unsplash.com/photo-1583608205776-bfd35f0d9f83?auto=format&fit=crop&w=400&q=80',
    priceModifier: 2800
  }, {
    id: 'pkg-cr-2-1',
    code: 'CR 2.1',
    thumbnail: 'https://images.unsplash.com/photo-1564013799919-ab600027ffc6?auto=format&fit=crop&w=400&q=80',
    priceModifier: 1800
  }, {
    id: 'pkg-cr-3-1',
    code: 'CR 3.1',
    thumbnail: 'https://images.unsplash.com/photo-1570129477492-45c003edd2be?auto=format&fit=crop&w=400&q=80',
    priceModifier: 3400
  }],
  'elev-craftsman': [{
    id: 'pkg-vfh-1-1',
    code: 'VFH 1.1',
    thumbnail: 'https://images.unsplash.com/photo-1600566753190-17f0baa2a6c3?auto=format&fit=crop&w=400&q=80',
    priceModifier: 0
  }, {
    id: 'pkg-vfh-3-1',
    code: 'VFH 3.1',
    thumbnail: 'https://images.unsplash.com/photo-1600585154340-be6161a56a0c?auto=format&fit=crop&w=400&q=80',
    priceModifier: 1500
  }, {
    id: 'pkg-vfh-4-1',
    code: 'VFH 4.1',
    thumbnail: 'https://images.unsplash.com/photo-1576941089067-2de3c901e126?auto=format&fit=crop&w=400&q=80',
    priceModifier: 2200
  }, {
    id: 'pkg-hfh-2-1',
    code: 'HFH 2.1',
    thumbnail: 'https://images.unsplash.com/photo-1605114603419-0dd2d23a06d4?auto=format&fit=crop&w=400&q=80',
    priceModifier: 1800
  }, {
    id: 'pkg-hfh-3-1',
    code: 'HFH 3.1',
    thumbnail: 'https://images.unsplash.com/photo-1568605114967-8130f3a36994?auto=format&fit=crop&w=400&q=80',
    priceModifier: 2600
  }, {
    id: 'pkg-hfh-4-1',
    code: 'HFH 4.1',
    thumbnail: 'https://images.unsplash.com/photo-1518780664697-55e3ad937233?auto=format&fit=crop&w=400&q=80',
    priceModifier: 3200
  }],
  'elev-farmhouse': [{
    id: 'pkg-mfh-1-1',
    code: 'MFH 1.1',
    thumbnail: 'https://images.unsplash.com/photo-1600607687939-ce8a6c25118c?auto=format&fit=crop&w=400&q=80',
    priceModifier: 0
  }, {
    id: 'pkg-mfh-2-1',
    code: 'MFH 2.1',
    thumbnail: 'https://images.unsplash.com/photo-1564013799919-ab600027ffc6?auto=format&fit=crop&w=400&q=80',
    priceModifier: 1400
  }, {
    id: 'pkg-mfh-3-1',
    code: 'MFH 3.1',
    thumbnail: 'https://images.unsplash.com/photo-1580587771525-78b9dba3b914?auto=format&fit=crop&w=400&q=80',
    priceModifier: 2900
  }],
  'elev-georgian': [{
    id: 'pkg-gc-1-1',
    code: 'GC 1.1',
    thumbnail: 'https://images.unsplash.com/photo-1600573472550-8090b5e0745e?auto=format&fit=crop&w=400&q=80',
    priceModifier: 0
  }, {
    id: 'pkg-gc-2-1',
    code: 'GC 2.1',
    thumbnail: 'https://images.unsplash.com/photo-1572120360610-d971b9d7767c?auto=format&fit=crop&w=400&q=80',
    priceModifier: 2200
  }]
};
import { useTheme } from '../styles/ThemeContext';
import { DesignModeSelectorAlt } from './DesignModeSelectorAlt';
import { DesignMode, NavigationMode } from './DesignModeSelector';
import { Play, Square } from 'lucide-react';
import { DollhouseOverlay } from './DollhouseOverlay';
function InteriorViewerContent() {
  const {
    isDark
  } = useTheme();
  const [selectedItem, setSelectedItem] = useState<InteriorMenuItem | null>(null);
  const [designMode, setDesignMode] = useState<DesignMode>('interior');
  const [navigationMode, setNavigationMode] = useState<NavigationMode>('walk');
  const [selectedElevationId, setSelectedElevationId] = useState<string | null>(null);
  const [selectedPaletteId, setSelectedPaletteId] = useState<string | null>(null);
  const [selectedPackageByElevation, setSelectedPackageByElevation] = useState<Record<string, string | null>>({});
  // Package pane can be dismissed; re-opens when a new elevation is selected
  const [packageOverlayOpen, setPackageOverlayOpen] = useState(true);
  const [showSettings, setShowSettings] = useState(false);
  const [settingsTab, setSettingsTab] = useState<'settings' | 'controls'>('settings');
  const [showNavigateModal, setShowNavigateModal] = useState(false);
  const [isAssistantActive, setIsAssistantActive] = useState(false);
  const [showRoomsMenu, setShowRoomsMenu] = useState(false);
  const [showNavigation, setShowNavigation] = useState(false);
  const [showRoomsPanel, setShowRoomsPanel] = useState(false);
  const [isGuidedTourActive, setIsGuidedTourActive] = useState(false);
  const [tourRoomIndex, setTourRoomIndex] = useState(0);
  const [currentCategory, setCurrentCategory] = useState<string>('Kitchen');
  const {
    addSelection,
    selections
  } = useSelections();
  // Tour toast notification
  const [tourToast, setTourToast] = useState<{
    message: string;
    subtitle: string;
    type: 'start' | 'stop';
  } | null>(null);
  useEffect(() => {
    if (tourToast) {
      const timer = setTimeout(() => setTourToast(null), 2500);
      return () => clearTimeout(timer);
    }
  }, [tourToast]);
  // Auto-launch guided tour from URL param (?mode=guided)
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get('mode') === 'guided') {
      const timer = setTimeout(() => {
        handleNavigationModeChange('tour');
      }, 500);
      return () => clearTimeout(timer);
    }
  }, []);
  // Mutually exclusive toggle handlers
  const handleSetShowNavigation = (show: boolean) => {
    if (show) setShowRoomsPanel(false);
    setShowNavigation(show);
  };
  const handleSetShowRoomsPanel = (show: boolean) => {
    if (show) setShowNavigation(false);
    setShowRoomsPanel(show);
  };
  // Handle Design Mode Change
  const handleDesignModeChange = (mode: DesignMode) => {
    setDesignMode(mode);
    setShowRoomsPanel(false);
    setShowNavigation(false);
    setSelectedItem(null);
  };
  // Handle Navigation Mode Change
  const handleNavigationModeChange = (mode: NavigationMode) => {
    setNavigationMode(mode);
    // Reset states
    setShowRoomsPanel(false);
    setShowNavigation(false);
    if (mode === 'tour') {
      handleStartGuidedTour();
      setIsStepByStep(false);
    } else if (mode === 'step-by-step') {
      setIsGuidedTourActive(false);
      if (!isStepByStep) {
        handleToggleStepMode();
      }
    } else {
      // Walk mode
      setIsGuidedTourActive(false);
      setIsStepByStep(false);
      setSelectedItem(null);
    }
  };
  // Step-by-Step Mode State
  const [isStepByStep, setIsStepByStep] = useState(false);
  // Flatten menu data for step-by-step navigation
  const flatSteps = useMemo(() => {
    const steps: {
      item: InteriorMenuItem;
      category: string;
    }[] = [];
    const traverse = (items: InteriorMenuItem[], parentCategory: string) => {
      items.forEach(item => {
        if (item.type === 'item' && (item.options || !item.children)) {
          steps.push({
            item,
            category: parentCategory
          });
        }
        if (item.children) {
          traverse(item.children, item.type === 'category' ? item.name : parentCategory);
        }
      });
    };
    traverse(interiorMenuData, 'General');
    return steps;
  }, []);
  const customizationPercent = useMemo(() => {
    if (flatSteps.length === 0) return 0;
    const customizedCount = selections.length;
    return Math.round(customizedCount / flatSteps.length * 100);
  }, [flatSteps.length, selections.length]);
  const currentStepIndex = useMemo(() => {
    if (!selectedItem) return -1;
    return flatSteps.findIndex(step => step.item.id === selectedItem.id);
  }, [selectedItem, flatSteps]);
  const handleNextStep = () => {
    const nextIndex = currentStepIndex + 1;
    if (nextIndex < flatSteps.length) {
      const nextStep = flatSteps[nextIndex];
      setSelectedItem(nextStep.item);
      setCurrentCategory(nextStep.category);
    }
  };
  const handlePrevStep = () => {
    const prevIndex = currentStepIndex - 1;
    if (prevIndex >= 0) {
      const prevStep = flatSteps[prevIndex];
      setSelectedItem(prevStep.item);
      setCurrentCategory(prevStep.category);
    }
  };
  const handleToggleStepMode = () => {
    if (!isStepByStep) {
      if (!selectedItem && flatSteps.length > 0) {
        setSelectedItem(flatSteps[0].item);
        setCurrentCategory(flatSteps[0].category);
      }
      setIsStepByStep(true);
      setNavigationMode('step-by-step');
    } else {
      setIsStepByStep(false);
      setNavigationMode('walk');
    }
  };
  const handleSelectItem = (item: InteriorMenuItem) => {
    setSelectedItem(item);
    const findCategory = (items: InteriorMenuItem[], targetId: string, parentName?: string): string | null => {
      for (const menuItem of items) {
        if (menuItem.id === targetId) {
          return parentName || menuItem.name;
        }
        if (menuItem.children) {
          const found = findCategory(menuItem.children, targetId, menuItem.type === 'category' ? menuItem.name : parentName);
          if (found) return found;
        }
      }
      return null;
    };
    const category = findCategory(interiorMenuData, item.id) || 'General';
    setCurrentCategory(category);
  };
  const handleSelectOption = (_optionId: string) => {
    // The OptionsGallery is the single source of truth for adding selections —
    // it computes the correct price (including per-sq-ft rate × area) and calls
    // addSelection itself. This handler is only a notification hook; re-adding
    // here would overwrite the gallery's pricing with a flat value.
  };
  const handleExit = () => {
    window.location.reload();
  };
  const handleStartGuidedTour = () => {
    if (isGuidedTourActive) {
      setIsGuidedTourActive(false);
      setNavigationMode('walk');
      setTourToast({
        message: 'Tour Ended',
        subtitle: 'Back to customization',
        type: 'stop'
      });
    } else {
      setTourRoomIndex(0);
      setIsGuidedTourActive(true);
      setNavigationMode('tour');
      setShowNavigation(false);
      setShowRoomsPanel(false);
      setSelectedItem(null);
      setIsStepByStep(false);
      setTourToast({
        message: 'Guided Tour Started',
        subtitle: 'Sit back and enjoy the walkthrough',
        type: 'start'
      });
    }
  };
  const handleNextTourRoom = () => {
    if (tourRoomIndex < tourStops.length - 1) {
      setTourRoomIndex(tourRoomIndex + 1);
    }
  };
  const handlePrevTourRoom = () => {
    if (tourRoomIndex > 0) {
      setTourRoomIndex(tourRoomIndex - 1);
    }
  };
  const handleStopTour = () => {
    setIsGuidedTourActive(false);
    setNavigationMode('walk');
  };
  const tourStops = ['Kitchen', 'Living Room', 'Primary Bedroom', 'Ensuite', 'Bedroom 2', 'Bedroom 3', 'Laundry', 'Basement'];
  const handleSelectRoom = (room: string) => {
    console.log('Teleport to:', room);
  };
  const handleOpenSettings = () => {
    if (showSettings && settingsTab === 'settings') {
      setShowSettings(false);
    } else {
      setSettingsTab('settings');
      setShowSettings(true);
    }
  };
  const handleOpenControls = () => {
    if (showSettings && settingsTab === 'controls') {
      setShowSettings(false);
    } else {
      setSettingsTab('controls');
      setShowSettings(true);
    }
  };
  const handleOpenNavigate = () => {
    setShowNavigateModal(true);
    setShowSettings(false);
  };
  const handleToggleAssistant = () => {
    setIsAssistantActive(prev => {
      if (!prev) {
        setShowSettings(false);
        setShowNavigateModal(false);
        setShowNavigation(false);
        setShowRoomsPanel(false);
      }
      return !prev;
    });
  };
  const currentMenuData = designMode === 'exterior' ? exteriorMenuData : interiorMenuData;
  const isLayoutMode = currentCategory === 'Layouts' || selectedItem?.id?.startsWith('floorplan');
  // Calculate active option ID for the overlay
  let activeOptionId = null;
  if (isLayoutMode && selectedItem?.options) {
    const selection = selections.find(s => s.itemName === selectedItem.name);
    if (selection) {
      activeOptionId = selection.optionId;
    } else {
      const defaultOpt = selectedItem.options.find(o => o.selected);
      if (defaultOpt) activeOptionId = defaultOpt.id;
    }
  }
  const bgImage = designMode === 'exterior' ? 'https://images.unsplash.com/photo-1600585154340-be6161a56a0c?auto=format&fit=crop&w=1920&q=80' : isLayoutMode ? 'https://cdn.magicpatterns.com/uploads/inZox1BHnV1wa6HrGFqPnH/dollhouse-bg.png' : 'https://images.unsplash.com/photo-1600210492486-724fe5c67fb0?auto=format&fit=crop&w=1920&q=80';
  const layoutProps = {
    menuData: currentMenuData,
    selectedItem,
    onSelectItem: handleSelectItem,
    onSelectOption: handleSelectOption,
    onCloseOption: () => setSelectedItem(null),
    currentCategory,
    showSettings,
    setShowSettings,
    showRoomsMenu,
    setShowRoomsMenu,
    onExit: handleExit,
    showNavigation,
    setShowNavigation: handleSetShowNavigation,
    showRoomsPanel,
    setShowRoomsPanel: handleSetShowRoomsPanel,
    onSelectRoom: handleSelectRoom,
    onStartGuidedTour: handleStartGuidedTour,
    isGuidedTourActive,
    tourRoomIndex,
    totalTourRooms: tourStops.length,
    currentTourRoomName: tourStops[tourRoomIndex],
    onNextTourRoom: handleNextTourRoom,
    onPrevTourRoom: handlePrevTourRoom,
    onStopTour: handleStopTour,
    isStepByStep,
    stepIndex: currentStepIndex !== -1 ? currentStepIndex : 0,
    totalSteps: flatSteps.length,
    currentStepItem: selectedItem,
    currentStepCategory: currentCategory,
    onNextStep: handleNextStep,
    onPrevStep: handlePrevStep,
    onToggleStepMode: handleToggleStepMode,
    designMode,
    onDesignModeChange: handleDesignModeChange,
    customizationPercent,
    onOpenSettings: handleOpenSettings,
    onOpenControls: handleOpenControls,
    onOpenNavigate: handleOpenNavigate,
    settingsTab,
    isAssistantActive,
    onToggleAssistant: handleToggleAssistant,
    hideAssistantButton: true
  };
  return <div className="relative w-full h-screen overflow-hidden bg-slate-900 font-sans" data-id="element-1084">
      {/* 3D Viewer Background */}
      <div className="absolute inset-0 z-0" data-id="element-1085">
        <img src={bgImage} alt={designMode === 'exterior' ? 'Exterior view' : 'Interior view'} className={`w-full h-full object-cover transition-all duration-500 ${isLayoutMode ? 'object-[70%_center] scale-105' : ''}`} style={isLayoutMode ? {
        position: 'absolute',
        right: '-250px'
      } : undefined} data-id="element-1086" />
        <DollhouseOverlay isVisible={isLayoutMode} selectedOptionId={activeOptionId} data-id="element-1087" />
        <div className="absolute inset-0 bg-gradient-to-b from-black/20 via-transparent to-black/40 pointer-events-none" data-id="element-1088" />
      </div>

      {/* Design Mode Selector (Left Rail) */}
      <DesignModeSelectorAlt activeMode={designMode} onSelectMode={handleDesignModeChange} navigationMode={navigationMode} onNavigationModeChange={handleNavigationModeChange} isAssistantActive={isAssistantActive} onToggleAssistant={handleToggleAssistant} onNavigatePanelOpen={() => {
      setShowSettings(false);
      setShowNavigateModal(false);
      setShowNavigation(false);
      setShowRoomsPanel(false);
    }} data-id="element-1089" />

      {/* Layout */}
      <LayoutStandard {...layoutProps} data-id="element-1090" />

      {/* Exterior Elevation + Palette Gallery */}
      <AnimatePresence data-id="element-1091">
        {designMode === 'exterior' && (() => {
        const activePackages = selectedElevationId ? EXTERIOR_PACKAGES[selectedElevationId] ?? [] : [];
        const activePackageId = selectedElevationId ? selectedPackageByElevation[selectedElevationId] ?? null : null;
        const activeElevationName = EXTERIOR_ELEVATIONS.find(e => e.id === selectedElevationId)?.name ?? '';
        const selectPackage = (pkg: ExteriorPackageOption) => {
          if (!selectedElevationId) return;
          setSelectedPackageByElevation(prev => ({
            ...prev,
            [selectedElevationId]: pkg.id
          }));
        };
        const hasPackages = activePackages.length > 0;
        return <>
                <ExteriorGallery elevations={EXTERIOR_ELEVATIONS} selectedElevationId={selectedElevationId} onSelectElevation={elevation => {
            setSelectedElevationId(elevation.id);
            setPackageOverlayOpen(true);
          }} palettes={exteriorPalettes} selectedPaletteId={selectedPaletteId} onSelectPalette={palette => setSelectedPaletteId(palette.id)} modelName={models[0].name} selectedPackageId={activePackageId} onSelectPackage={selectPackage} data-id="element-1092" />

                {/* Right-side inset package pane (sits left of the utility rail,
                            stops above the cart pill — the approved pattern).
                            Collapses to a small re-open tab instead of closing fully. */}
                {hasPackages && <ExteriorPackagePane key={`pkg-pane-${selectedElevationId}`} elevationName={activeElevationName} packages={activePackages} selectedPackageId={activePackageId} onSelectPackage={selectPackage} isOpen={packageOverlayOpen} onToggle={() => setPackageOverlayOpen(o => !o)} data-id="element-1093" />}
              </>;
      })()}
      </AnimatePresence>

      {/* Global Overlays */}
      <SettingsMenu isOpen={showSettings} onClose={() => setShowSettings(false)} initialTab={settingsTab} onTabChange={tab => setSettingsTab(tab)} data-id="element-1094" />

      <NavigationGuideModal isOpen={showNavigateModal} onClose={() => setShowNavigateModal(false)} data-id="element-1095" />

      <AssistantOverlay isActive={isAssistantActive} onDeactivate={() => setIsAssistantActive(false)} data-id="element-1096" />

      <SelectionsWidget onSwap={(cat, itemName) => {
      const findMenuItem = (items: InteriorMenuItem[], target: string): InteriorMenuItem | null => {
        for (const item of items) {
          if (item.name === target) return item;
          if (item.children) {
            const found = findMenuItem(item.children, target);
            if (found) return found;
          }
        }
        return null;
      };
      const menuItem = findMenuItem(interiorMenuData, itemName);
      if (menuItem) {
        handleSelectItem(menuItem);
      }
    }} isGalleryOpen={!!selectedItem?.options} data-id="element-1097" />

      {/* Tour Toast Notification */}
      <AnimatePresence data-id="element-1098">
        {tourToast && <motion.div initial={{
        opacity: 0,
        y: -20,
        scale: 0.95
      }} animate={{
        opacity: 1,
        y: 0,
        scale: 1
      }} exit={{
        opacity: 0,
        y: -20,
        scale: 0.95
      }} transition={{
        duration: 0.3,
        ease: [0.22, 1, 0.36, 1]
      }} className="fixed top-8 left-1/2 -translate-x-1/2 z-[100]" data-id="element-1099">
            <div className={`flex items-center gap-3 px-5 py-3 rounded-xl backdrop-blur-xl border shadow-2xl ${isDark ? 'bg-slate-900/90 border-white/10' : 'bg-white/90 border-white/40'}`} data-id="element-1100">
              <div className={`p-2 rounded-lg ${tourToast.type === 'start' ? isDark ? 'bg-cyan-500/20' : 'bg-teal-500/15' : isDark ? 'bg-white/10' : 'bg-gray-100'}`} data-id="element-1101">
                {tourToast.type === 'start' ? <Play className={`w-4 h-4 ${isDark ? 'text-cyan-400' : 'text-teal-600'}`} data-id="element-1102" /> : <Square className={`w-4 h-4 ${isDark ? 'text-white/60' : 'text-gray-500'}`} data-id="element-1103" />}
              </div>
              <div data-id="element-1104">
                <p className={`text-sm font-semibold ${isDark ? 'text-white' : 'text-gray-900'}`} data-id="element-1105">
                  {tourToast.message}
                </p>
                <p className={`text-xs ${isDark ? 'text-white/50' : 'text-gray-500'}`} data-id="element-1106">
                  {tourToast.subtitle}
                </p>
              </div>
            </div>
          </motion.div>}
      </AnimatePresence>
    </div>;
}
export function InteriorViewer() {
  return <SelectionsProvider data-id="element-1107">
      <InteriorViewerContent data-id="element-1108" />
    </SelectionsProvider>;
}